/**
 * InterpreterMessagingApi.js
 *
 * REST API utilities for the Messaging System.
 * Provides functions for conversation management, message operations, and error handling.
 *
 * All functions require a valid JWT token for authentication.
 */

import { ApiDomainUrl } from './utils/Common';

/**
 * Get the messaging API base URL
 * Uses the app's configured API domain
 */
function getMessagingApiBase() {
  return `${ApiDomainUrl()}/api/Messaging`;
}

/**
 * Error codes for API operations
 */
export const API_ERROR_CODES = {
  AUTH_EXPIRED: 'AUTH_EXPIRED',
  AUTH_FORBIDDEN: 'AUTH_FORBIDDEN',
  NETWORK_ERROR: 'NETWORK_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  NOT_FOUND: 'NOT_FOUND',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
};

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(message, code, status, data = null) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.data = data;
  }
}

/**
 * Generic API call wrapper with error handling
 *
 * @param {string} url - The URL to fetch
 * @param {object} options - Fetch options (method, headers, body, etc.)
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {ApiError} - On any error
 */
export async function apiCall(url, options = {}) {
  try {
    const response = await fetch(url, options);

    // Handle authentication errors
    if (response.status === 401) {
      throw new ApiError(
        'Authentication expired. Please log in again.',
        API_ERROR_CODES.AUTH_EXPIRED,
        401
      );
    }

    // Handle forbidden errors
    if (response.status === 403) {
      throw new ApiError(
        'You do not have permission to perform this action.',
        API_ERROR_CODES.AUTH_FORBIDDEN,
        403
      );
    }

    // Handle not found errors
    if (response.status === 404) {
      throw new ApiError(
        'The requested resource was not found.',
        API_ERROR_CODES.NOT_FOUND,
        404
      );
    }

    // Handle validation errors
    if (response.status === 400) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || 'Validation error. Please check your input.',
        API_ERROR_CODES.VALIDATION_ERROR,
        400,
        errorData
      );
    }

    // Handle server errors
    if (response.status >= 500) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || 'Server error. Please try again later.',
        API_ERROR_CODES.SERVER_ERROR,
        response.status,
        errorData
      );
    }

    // Handle other non-OK responses
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        errorData.message || `HTTP ${response.status}: ${response.statusText}`,
        API_ERROR_CODES.UNKNOWN_ERROR,
        response.status,
        errorData
      );
    }

    // Parse and return JSON response
    return await response.json();
  } catch (error) {
    // Re-throw ApiError instances
    if (error instanceof ApiError) {
      throw error;
    }

    // Handle network errors
    if (error.name === 'TypeError' || error.message.includes('fetch')) {
      throw new ApiError(
        'Network error. Please check your internet connection.',
        API_ERROR_CODES.NETWORK_ERROR,
        0,
        error
      );
    }

    // Handle unknown errors
    throw new ApiError(
      error.message || 'An unexpected error occurred.',
      API_ERROR_CODES.UNKNOWN_ERROR,
      0,
      error
    );
  }
}

/**
 * Get or create a direct conversation with another user
 *
 * @param {string} token - JWT authentication token
 * @param {string} otherUserId - The UserId of the other participant
 * @returns {Promise<object>} - Response with conversation details
 * @returns {object} response.data - The CreateDirectConversationResponse object
 * @returns {string} response.data.conversationId - The conversation ID
 * @returns {boolean} response.data.wasCreated - True if conversation was newly created
 *
 * @example
 * const result = await getOrCreateDirectConversation(token, 'user-123');
 * console.log(result.data.conversationId); // "conv-456"
 * console.log(result.data.wasCreated); // true
 */
export async function getOrCreateDirectConversation(token, otherUserId) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!otherUserId) {
    throw new ApiError('otherUserId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  return await apiCall(`${getMessagingApiBase()}/conversations/direct`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({ otherUserId }),
  });
}

/**
 * Fetch messages for a conversation with cursor-based pagination
 *
 * @param {string} token - JWT authentication token
 * @param {object} filter - Message filter parameters
 * @param {string} filter.conversationId - The conversation ID
 * @param {string} [filter.cursorMessageId] - Message ID to start from (for pagination)
 * @param {string} [filter.cursorSentAt] - Timestamp to start from (for pagination)
 * @param {number} [filter.pageSize=20] - Number of messages to fetch
 * @returns {Promise<object>} - Response with message list
 * @returns {object} response.data - The MessageListResponse object
 * @returns {Array} response.data.messages - Array of Message objects
 * @returns {boolean} response.data.hasMore - True if more messages are available
 * @returns {string} [response.data.nextCursorMessageId] - Next page cursor message ID
 * @returns {string} [response.data.nextCursorSentAt] - Next page cursor timestamp
 *
 * @example
 * const result = await getMessages(token, {
 *   conversationId: 'conv-123',
 *   pageSize: 20
 * });
 * console.log(result.data.messages.length); // 20
 * console.log(result.data.hasMore); // true
 */
export async function getMessages(token, filter) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!filter || !filter.conversationId) {
    throw new ApiError('conversationId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  // Set default page size
  const messageFilter = {
    ...filter,
    pageSize: filter.pageSize || 20,
  };

  return await apiCall(`${getMessagingApiBase()}/messages`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(messageFilter),
  });
}

/**
 * Send a message to a conversation
 *
 * @param {string} token - JWT authentication token
 * @param {object} request - Message send request
 * @param {string} request.conversationId - The conversation ID
 * @param {string} request.messageText - The message text content
 * @param {string} [request.messageType='text'] - Message type (text, image, etc.)
 * @param {string} [request.attachmentUrl] - URL of attachment if any
 * @param {string} [request.attachmentName] - Name of attachment if any
 * @param {string} [request.replyToMessageId] - Message ID this is replying to
 * @returns {Promise<object>} - Response with sent message details
 * @returns {object} response.data - The Message object
 *
 * @example
 * const result = await sendMessage(token, {
 *   conversationId: 'conv-123',
 *   messageText: 'Hello, world!'
 * });
 * console.log(result.data.messageId); // "msg-456"
 */
export async function sendMessage(token, request) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!request || !request.conversationId) {
    throw new ApiError('conversationId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!request.messageText || !request.messageText.trim()) {
    throw new ApiError('messageText is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  // Set default message type
  const sendRequest = {
    ...request,
    messageType: request.messageType || 'text',
  };

  return await apiCall(`${getMessagingApiBase()}/messages/send`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(sendRequest),
  });
}

/**
 * Mark messages in a conversation as read
 *
 * @param {string} token - JWT authentication token
 * @param {object} request - Mark as read request
 * @param {string} request.conversationId - The conversation ID
 * @param {string} [request.lastReadMessageId] - ID of the last message read
 * @returns {Promise<object>} - Response confirming read status
 *
 * @example
 * await markMessagesAsRead(token, {
 *   conversationId: 'conv-123',
 *   lastReadMessageId: 'msg-456'
 * });
 */
export async function markMessagesAsRead(token, request) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!request || !request.conversationId) {
    throw new ApiError('conversationId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  return await apiCall(`${getMessagingApiBase()}/messages/read`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(request),
  });
}

/**
 * Get all conversations for the current user
 *
 * @param {string} token - JWT authentication token
 * @returns {Promise<object>} - Response with conversation list
 * @returns {Array} response.data - Array of Conversation objects
 *
 * @example
 * const result = await getConversations(token);
 * console.log(result.data.length); // 5
 */
export async function getConversations(token) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  return await apiCall(`${getMessagingApiBase()}/conversations`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

/**
 * Get a specific conversation by ID
 *
 * @param {string} token - JWT authentication token
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<object>} - Response with conversation details
 * @returns {object} response.data - The Conversation object
 *
 * @example
 * const result = await getConversation(token, 'conv-123');
 * console.log(result.data.title); // "John Doe"
 */
export async function getConversation(token, conversationId) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!conversationId) {
    throw new ApiError('conversationId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  return await apiCall(`${getMessagingApiBase()}/conversations/${conversationId}`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

/**
 * Get participants for a conversation
 *
 * @param {string} token - JWT authentication token
 * @param {string} conversationId - The conversation ID
 * @returns {Promise<object>} - Response with participant list
 * @returns {Array} response.data - Array of ConversationParticipant objects
 *
 * @example
 * const result = await getConversationParticipants(token, 'conv-123');
 * console.log(result.data.length); // 2
 */
export async function getConversationParticipants(token, conversationId) {
  if (!token) {
    throw new ApiError('Token is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }
  if (!conversationId) {
    throw new ApiError('conversationId is required', API_ERROR_CODES.VALIDATION_ERROR, 0);
  }

  return await apiCall(`${getMessagingApiBase()}/conversations/${conversationId}/participants`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });
}

/**
 * Helper function to check if an error requires re-authentication
 *
 * @param {Error} error - The error to check
 * @returns {boolean} - True if user should re-authenticate
 */
export function requiresReauthentication(error) {
  if (error instanceof ApiError) {
    return error.code === API_ERROR_CODES.AUTH_EXPIRED;
  }
  return false;
}

/**
 * Helper function to check if an error is retryable
 *
 * @param {Error} error - The error to check
 * @returns {boolean} - True if operation can be retried
 */
export function isRetryableError(error) {
  if (error instanceof ApiError) {
    return error.code === API_ERROR_CODES.NETWORK_ERROR ||
           error.code === API_ERROR_CODES.SERVER_ERROR;
  }
  return false;
}

/**
 * Get a user-friendly error message from an error
 *
 * @param {Error} error - The error to get message from
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error) {
  if (error instanceof ApiError) {
    return error.message;
  }
  return 'An unexpected error occurred. Please try again.';
}
